import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../lib/api/client';

export interface Subscription {
  id: string;
  user_id: number;
  plan: string;
  status: 'active' | 'expired' | 'cancelled';
  started_at: string;
  expires_at: string;
  amount: number;
  duration_days: number;
}

export interface PaymentHistory {
  id: string;
  amount: number;
  plan: string;
  started_at: string;
  expires_at: string;
  status: string;
  payment_status: string;
  payment_method: string;
  order_uuid: string;
  paid_at: string;
}

interface SubscriptionStats {
  totalSpent: number;
  paymentsCount: number;
  activeSubscription: Subscription | null;
  paymentHistory: PaymentHistory[];
}

export function useSubscription() {
  const { isAuthenticated } = useAuth();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistory[]>([]);
  const [stats, setStats] = useState<SubscriptionStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

  const fetchCurrentSubscription = useCallback(async () => {
    try {
      console.log('💳 [fetchCurrentSubscription] Buscando assinatura atual...');

      const response = await apiClient.get('/payment/subscription/current');

      if (response.data) {
        const subscriptionData = response.data.data || response.data;
        console.log(`✅ [fetchCurrentSubscription] Assinatura carregada:`, subscriptionData);
        setSubscription(subscriptionData);
        setError(null);
        return subscriptionData;
      }
    } catch (err: any) {
      const message = err instanceof Error ? err.message : 'Erro ao buscar assinatura';
      console.error('❌ [fetchCurrentSubscription] Erro:', message);
      setError(message);
      throw err;
    }
  }, []);

  const fetchPaymentHistory = useCallback(async () => {
    try {
      console.log('💳 [fetchPaymentHistory] Buscando histórico...');

      const response = await apiClient.get('/payment/subscription/history');

      if (response.data) {
        const history = response.data.data || [];
        console.log('💳 [fetchPaymentHistory] Histórico carregado');
        setPaymentHistory(history);

        const totalSpent = (response.data.data || []).reduce(
          (acc: number, payment: PaymentHistory) => acc + payment.amount,
          0
        );

        setStats({
          totalSpent,
          paymentsCount: response.data.data?.length || 0,
          activeSubscription: null,
          paymentHistory: response.data.data || [],
        });

        setError(null);
      }
    } catch (err: any) {
      const message = err instanceof Error ? err.message : 'Erro ao buscar histórico';
      console.error('❌ [fetchPaymentHistory] Erro:', message);
      setError(message);
      throw err;
    }
  }, []);

  const fetchAll = useCallback(async () => {
    if (!isAuthenticated) {
      console.log('⏭️ [useSubscription] Ignorando fetch - não autenticado');
      return;
    }

    console.log('💳 [useSubscription] Carregando assinatura e histórico...');
    setIsLoading(true);
    setHasAttemptedFetch(true);

    try {
      await Promise.all([fetchCurrentSubscription(), fetchPaymentHistory()]);
      console.log('✅ [useSubscription] Dados carregados com sucesso');
    } catch (fetchError) {
      console.error('❌ [useSubscription] Erro ao carregar:', fetchError);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, fetchCurrentSubscription, fetchPaymentHistory]);

  const isSubscriptionActive = useCallback(() => {
    if (!subscription) return false;
    return (
      subscription.status === 'active' &&
      new Date(subscription.expires_at) > new Date()
    );
  }, [subscription]);

  const daysUntilExpiration = useCallback(() => {
    if (!subscription) return 0;
    const expiresAt = new Date(subscription.expires_at);
    const today = new Date();
    const diffTime = expiresAt.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }, [subscription]);

  // 🔄 Auto-fetch subscription quando usuário faz login
  useEffect(() => {
    if (isAuthenticated && !hasAttemptedFetch) {
      console.log('🔄 [useSubscription] Auto-fetching após autenticação');
      fetchAll();
    }
  }, [isAuthenticated, hasAttemptedFetch, fetchAll]);

  // Calcular isActive dinamicamente, retornando false enquanto carrega
  const isActive = isSubscriptionActive();
  const isSubscriptionActiveState = isLoading || !hasAttemptedFetch ? false : isActive;

  return {
    subscription,
    paymentHistory,
    stats,
    isLoading,
    error,
    hasAttemptedFetch,
    isSubscriptionActive: isSubscriptionActiveState,
    daysUntilExpiration: daysUntilExpiration(),
    refetch: fetchAll,
    fetchCurrentSubscription,
    fetchPaymentHistory,
  };
}
